import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, switchMap, timeout } from 'rxjs/operators';
import { AuthService } from '../../features/auth/@core/services/auth.service';
import { URLS } from '../../shared/enums/urls.enum';
import { INewAccessToken } from '../interfaces/new-access-token.interface';
import { AccountService } from '../services/account.service';
import { AuthStateService } from '../services/auth-state.service';
import { TokenService } from '../services/token.service';

export const tokenInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const accountService = inject(AccountService);
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const authStateService = inject(AuthStateService);
  const router = inject(Router);

  const addTokenToRequest = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
    return request.clone({
      setHeaders: {
        Authorization: token,
        'Access-Control-Allow-Origin': '*',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
  };

  const handleAuthError = (error: unknown): Observable<never> => {
    console.error('Authentication error:', error);
    authService.clearAuthData();
    router.navigate([URLS.LOGIN_PATH]);
    return throwError(() => new Error('Authentication failed'));
  };

  const handleRefreshToken = (): Observable<string> => {
    if (!authStateService.isTokenRefreshing()) {
      authStateService.setRefreshing(true);
      authStateService.setRefreshToken(null);

      const refreshToken = TokenService.getCurrentRefreshToken();
      if (!refreshToken) {
        return handleAuthError('No refresh token available');
      }

      return from(accountService.refreshAccessToken({ refreshToken: refreshToken })).pipe(
        switchMap((response: INewAccessToken) => {
          if (response && response.accessToken) {
            tokenService.setCurrentAccessToken(response.accessToken);
            authStateService.setRefreshToken(response.refreshToken);
            return of(response.accessToken);
          } else {
            authService.clearAuthData();
            return throwError(() => new Error('No refresh token available'));
          }
        }),
        catchError((refreshError: HttpErrorResponse): Observable<never> => {
          authStateService.setRefreshToken(null);
          authService.clearAuthData();
          return throwError(() => refreshError);
        }),
        finalize(() => {
          authStateService.setRefreshing(false);
        }),
      );
    }

    return authStateService.getRefreshToken$().pipe(
      timeout(30000),
      catchError((error) => {
        return handleAuthError(error);
      }),
    );
  };

  const accessToken = TokenService.getCurrentAccessToken();
  const refreshToken = TokenService.getCurrentRefreshToken();
  const initialRequest = accessToken ? addTokenToRequest(req, accessToken) : req;

  return next(initialRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && accessToken && refreshToken) {
        return handleRefreshToken().pipe(
          switchMap((newToken: string) => {
            return next(addTokenToRequest(req, newToken));
          }),
          catchError((refreshError: unknown) => {
            return handleAuthError(refreshError);
          }),
        );
      }
      if (error.status === 401) {
        return handleAuthError(error);
      }
      return throwError(() => error);
    }),
  );
};
